import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLms } from '../../context/LmsContext';
import { Card, PageHeader, StatusBadge } from '../../components/ui';
import { byId, getCourse, getLatestQuizAttempt, getLearningNavigation, getQuizAvailability } from '../../utils/lmsSelectors';

function NavigationButtons({ assignment, nav }) {
  return (
    <div className="learner-nav-row">
      {nav.previous ? (
        <Link className="button button-ghost" to={nav.previous.path}>Previous: {nav.previous.title}</Link>
      ) : (
        <Link className="button button-ghost" to={`/learner/course/${assignment.id}`}>Back to course outline</Link>
      )}
      {nav.next ? (
        <Link className="button button-secondary" to={nav.next.path}>Next: {nav.next.title}</Link>
      ) : (
        <Link className="button button-secondary" to={`/learner/course/${assignment.id}`}>Course summary</Link>
      )}
    </div>
  );
}

function formatSavedAt(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
  } catch (error) {
    return value;
  }
}

function resetReviewPath(assignment, result) {
  if (result?.reset?.type === 'lesson' && result.reset.targetLessonId) {
    return `/learner/lesson/${assignment.id}:${result.reset.targetLessonId}`;
  }
  return `/learner/course/${assignment.id}`;
}

export default function QuizPage() {
  const { quizAttemptId } = useParams();
  const [assignmentId, quizId] = quizAttemptId.split(':');
  const { data, currentUser, saveQuizAnswers, submitQuiz } = useLms();
  const navigate = useNavigate();
  const assignment = data.assignments.find((item) => item.id === assignmentId && item.assignedToUserId === currentUser.id);
  const quiz = byId(data.quizzes, quizId);
  const savedDraft = assignment?.quizAnswerDrafts?.[quizId] || null;
  const savedAnswers = savedDraft?.answers;
  const savedAt = savedDraft?.savedAt;
  const [answers, setAnswers] = useState(() => savedAnswers || {});
  const [result, setResult] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [isSavingAnswers, setIsSavingAnswers] = useState(false);
  const [isSubmittingQuiz, setIsSubmittingQuiz] = useState(false);

  useEffect(() => {
    setAnswers(savedAnswers || {});
    setResult(null);
    setSaveMessage(savedAt ? `Loaded saved answers from ${formatSavedAt(savedAt)}.` : '');
  }, [assignmentId, quizId, savedAnswers, savedAt]);

  if (!assignment || !quiz) return <Card>Quiz not found or access removed.</Card>;
  const course = getCourse(data, assignment.courseId);
  const latest = getLatestQuizAttempt(assignment, quiz.id);
  const attemptsUsed = assignment.quizAttempts.filter((attempt) => attempt.quizId === quiz.id).length;
  const nav = getLearningNavigation(data, assignment, 'quiz', quiz.id);
  const quizGate = getQuizAvailability(data, assignment, quiz);
  const answeredCount = Object.keys(answers).filter((questionId) => answers[questionId]).length;
  const allAnswered = answeredCount >= quiz.questions.length;

  const updateAnswer = (questionId, optionId) => {
    setAnswers((previous) => ({ ...previous, [questionId]: optionId }));
    setResult(null);
    setSaveMessage('Unsaved quiz answer changes. Click Save answers to keep them before leaving.');
  };

  const saveAnswers = async () => {
    if (isSavingAnswers) return;
    setIsSavingAnswers(true);
    const response = await saveQuizAnswers(assignment.id, quiz.id, answers, currentUser.id);
    setIsSavingAnswers(false);
    if (response.ok) {
      setSaveMessage(`Answers saved ${formatSavedAt(response.draft.savedAt)}. They are not graded until you submit the quiz.`);
    } else {
      setSaveMessage(response.message);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (isSubmittingQuiz) return;
    setIsSubmittingQuiz(true);
    const response = await submitQuiz(assignment.id, quiz.id, answers, currentUser.id);
    setIsSubmittingQuiz(false);
    if (response.ok) {
      setResult(response.attempt);
      setSaveMessage('');
    } else {
      setResult({ error: response.message, reset: response.reset });
    }
  };

  const continueAfterQuiz = () => {
    navigate(nav.next?.path || `/learner/course/${assignment.id}`);
  };

  if (!quizGate.canTake) {
    return (
      <>
        <PageHeader
          title={quiz.title}
          eyebrow={course?.title}
          description="This quiz is locked until the attached lesson or module is completed."
          action={<Link className="button button-secondary" to={`/learner/course/${assignment.id}`}>Back to course</Link>}
        />
        <div className="learner-nav-row">
          {nav.previous ? (
            <Link className="button button-ghost" to={nav.previous.path}>Previous: {nav.previous.title}</Link>
          ) : (
            <Link className="button button-ghost" to={`/learner/course/${assignment.id}`}>Back to course outline</Link>
          )}
        </div>
        <Card>
          <h2>Quiz locked</h2>
          <p>{quizGate.message || 'Complete the attached lesson or module before taking this quiz.'}</p>
          {quizGate.incompleteLessons?.length > 0 && (
            <div className="answer-review">
              <h3>Complete first</h3>
              {quizGate.incompleteLessons.map((lesson) => (
                <p key={lesson.id}>
                  <Link className="text-link strong-link" to={`/learner/lesson/${assignment.id}:${lesson.id}`}>{lesson.title}</Link>
                </p>
              ))}
            </div>
          )}
          <div className="button-row">
            <Link className="button button-primary" to={quizGate.incompleteLessons?.[0] ? `/learner/lesson/${assignment.id}:${quizGate.incompleteLessons[0].id}` : `/learner/course/${assignment.id}`}>
              {quizGate.incompleteLessons?.[0] ? 'Open required lesson' : 'Return to course'}
            </Link>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader title={quiz.title} eyebrow={course?.title} description={`Passing score ${quiz.passingScore}% • ${quiz.maxAttempts} max attempts • ${quiz.timeLimitMinutes || 'No'} minute limit`} action={<Link className="button button-secondary" to={`/learner/course/${assignment.id}`}>Back to course</Link>} />
      <NavigationButtons assignment={assignment} nav={nav} />
      <div className="grid two uneven">
        <Card>
          <h2>Quiz attempt</h2>
          <div className="builder-topline"><span>Attempts used: {attemptsUsed}/{quiz.maxAttempts}</span>{latest && <StatusBadge status={latest.status} />}</div>
          {savedDraft?.savedAt && <p className="muted small">Saved draft: {formatSavedAt(savedDraft.savedAt)}</p>}
          {saveMessage && <p className="muted small">{saveMessage}</p>}
          <form className="quiz-form" onSubmit={submit}>
            {quiz.questions.map((question, index) => (
              <fieldset key={question.id} className="question-card">
                <legend>{index + 1}. {question.prompt}</legend>
                {question.options.map((option) => (
                  <label key={option.id} className="check-row"><input required name={question.id} type="radio" value={option.id} checked={answers[question.id] === option.id} onChange={() => updateAnswer(question.id, option.id)} /> {option.optionText}</label>
                ))}
              </fieldset>
            ))}
            <div className="button-row">
              <button className="button button-secondary" type="button" disabled={!answeredCount || isSavingAnswers || isSubmittingQuiz} onClick={saveAnswers}>{isSavingAnswers ? 'Saving...' : 'Save answers'}</button>
              <button className="button button-primary" type="submit" disabled={result?.status === 'passed' || !allAnswered || isSubmittingQuiz}>{isSubmittingQuiz ? 'Submitting...' : 'Submit quiz'}</button>
            </div>
            {!allAnswered && <p className="muted small">Answer all questions before submitting. You can save partial answers anytime.</p>}
          </form>
        </Card>
        <Card>
          <h2>Result</h2>
          {!result && <p className="muted">Submit the quiz to see your score and next steps. Saved answers stay available when you return later.</p>}
          {result?.error && <p className="form-error">{result.error}</p>}
          {result?.scorePercent !== undefined && (
            <div className={`result-card ${result.status}`}>
              <StatusBadge status={result.status} />
              <h1>{result.scorePercent}%</h1>
              {result.resetTriggered ? (
                <p>{result.feedback}</p>
              ) : (
                <p>{result.status === 'passed' ? 'Passed. You can continue to the next required item.' : 'Failed. Review the feedback and retake if attempts remain.'}</p>
              )}
              <div className="button-row">
                <button className="button button-secondary" onClick={() => navigate(`/learner/course/${assignment.id}`)}>Return to course</button>
                {result.resetTriggered && <button className="button button-primary" onClick={() => navigate(resetReviewPath(assignment, result))}>Review reset {result.reset?.type || 'content'}</button>}
                {result.status === 'passed' && <button className="button button-primary" onClick={continueAfterQuiz}>{nav.next ? `Continue to ${nav.next.title}` : 'Finish course'}</button>}
              </div>
            </div>
          )}
          {quiz.showCorrectAnswers !== 'never' && result?.scorePercent !== undefined && (
            <div className="answer-review">
              <h3>Learning feedback</h3>
              {quiz.questions.map((question) => <p key={question.id}><strong>{question.prompt}</strong><br />{question.explanation}</p>)}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
